import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Button,
} from "reactstrap";
import * as Yup from "yup";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import CommonForm from "../../components/Common/CommonForm";
import ErrorAlert from "../../components/Common/Error/ErrorAlert";
import SuccessAlert from "../../components/Common/Success/SuccessAlert";
import {
  fetchDivisions,
  fetchRoles,
  registerUser,
  updateUser,
  fetchRanges,
  fetchRounds,
  fetchBeats,
  fetchCompartments,
  getAllUSer,
} from "../../store/actions";
import { postLogin } from "../../helpers/backend_helper";
import { toast } from "react-toastify";
import Select from "react-select";
import { convertToNumberIds } from "../../helpers/general";

// User form component for both create and edit operations
const UserForm = ({ isOpen, toggle, userData = null, onSuccess, onsubmit, setUpdated }) => {
  // State management
  const [originalFormConfigs, setOriginalFormConfigs] = useState({});
  const [formConfig, setFormConfig] = useState([]);
  const [formData, setFormData] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [submitTriggered, setSubmitTriggered] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  // State for multiselect values
  const [selectedDivisions, setSelectedDivisions] = useState([]);
  const [selectedRanges, setSelectedRanges] = useState([]);
  const [selectedRounds, setSelectedRounds] = useState([]);
  const [selectedBeats, setSelectedBeats] = useState([]);
  const [selectedCompartments, setSelectedCompartments] = useState([]);
  const [filteredCompartments, setFilteredCompartments] = useState([]);
  const [compartments, setCompartments] = useState([]);
  
  const dispatch = useDispatch();
  const { roles, divisions, ranges, rounds, beats, error } = useSelector(
    (state) => state.Common || {}
  );
  const { registrationError, message } = useSelector(
    (state) => state.account || {}
  );

  // Fetch compartments data
  useEffect(() => {
    const fetchCompartmentsData = async () => {
      try {
        const response = await fetch("Comps.json");
        const data = await response.json();
        setCompartments(data);
      } catch (error) {
        console.error("Error fetching compartments:", error);
      }
    };

    fetchCompartmentsData();
  }, []);

  // Dynamic validation schema generation
  const generateValidationSchema = (fields) => {
    const schemaFields = {};
    if (Array.isArray(fields)) {
      fields.forEach((field) => {
        let validator = field.required
          ? Yup.string().required(`${field.label} is required`)
          : Yup.string();

        if (field.type === "email") {
          validator = validator.email("Invalid email format");
        }

        if (field.maxlength) {
          validator = validator.max(
            parseInt(field.maxlength),
            `${field.label} must be at most ${field.maxlength} characters`
          );
        }

        schemaFields[field.inputName] = validator;
      });
    }

    return Yup.object().shape(schemaFields);
  };

  // Load dependent dropdowns data based on selected values
  const loadDependentDropdowns = (divisionIds, rangeIds, roundIds, beatIds) => {
    if (divisionIds || divisionIds.length > 0) {
      dispatch(fetchRanges(divisionIds));

      if (rangeIds || rangeIds.length > 0) {
        dispatch(fetchRounds(rangeIds));

        if (roundIds || roundIds.length > 0) {
          dispatch(fetchBeats(roundIds));

          if (beatIds ||beatIds?.length > 0) {
            // Fetch compartments for selected beats
            // beatIds?.forEach((beatId) => {
            //   if (beatId) {
                // dispatch(fetchCompartments(beatIds));
              // }
            // });
          }
        }
      }
    }
  };

  // Filter compartments based on selected beats
  const filterCompartmentsByBeats = () => {
    if (!selectedBeats || selectedBeats.length === 0 || !compartments) {
      setFilteredCompartments([]);
      return;
    }

    const selectedBeatIds = selectedBeats.map(beat => Number(beat.value));
    
    // Filter compartments for all selected beats
    const filtered = compartments.filter(comp => selectedBeatIds.includes(comp.beatId));
    setFilteredCompartments(filtered);
  };

  // Initialize form with userData if in edit mode
  useEffect(() => {
    if (userData) {
      setIsEditMode(true);
      const regionIdRoleIdUserId = userData["regionId#roleId#userId"];

      if (regionIdRoleIdUserId) {
        const [regionId, roleId, userId] = regionIdRoleIdUserId.split("#");

        // Parse division IDs if they exist
        let divisionIds = [];
        try {
          if (userData.divisionId) {
            // Handle if divisionId is a string with format "1#2#3"
            divisionIds = userData.divisionId.split("#");
          }
        } catch (e) {
          divisionIds = userData.divisionId ? [userData.divisionId] : [];
        }

        // Parse range IDs if they exist
        let rangeIds = [];
        try {
          if (userData.rangeId) {
            if (Array.isArray(userData.rangeId)) {
              rangeIds= userData.rangeId;
            }else if (typeof userData.rangeId === "string" && userData.rangeId.includes("#")) {
            // Handle if rangeId is a string with format "1#2#3"
            rangeIds = userData.rangeId.split("#");
            }else{
              rangeIds = [userData.rangeId];
            }
          }
        } catch (e) {
          rangeIds = userData.rangeId ? [userData.rangeId] : [];
        }

        // Parse round IDs if they exist
        let roundIds = [];
        try {
          if (userData.roundId) {
            if (Array.isArray(userData.roundId)) {
              roundIds= userData.roundId;
            }else if (typeof userData.roundId === "string" && userData.roundId.includes("#")) {
            roundIds = userData.roundId.split("#");
            }else{
              roundIds = [userData.roundId];
            }
          }
        } catch (e) {
          roundIds = userData.roundId ? [userData.roundId] : [];
        }

        // Parse beat IDs if they exist
        let beatIds = [];
        try {
          if (userData.beatId) {
            // Handle if beatId is already an array or a string that needs parsing
            beatIds = Array.isArray(userData.beatId) 
              ? userData.beatId 
              : userData.beatId.split("#");
          }
        } catch (e) {
          beatIds = userData.beatId ? [userData.beatId] : [];
        }

        // Parse compartment IDs if they exist
        let compartmentIds = [];
        try {
          if (userData.comptId) {
            if (Array.isArray(userData.comptId)) {
              compartmentIds= userData.comptId;
            }else if (typeof userData.comptId === "string" && userData.comptId.includes("#")) {
            compartmentIds = userData.comptId.split("#");
            }else{
              compartmentIds = [userData.comptId];
            }
          }
        } catch (e) {
          compartmentIds = userData.comptId ? [userData.comptId] : [];
        }

        setFormData({
          ...userData,
          role: roleId,
          division: divisionIds,
          range: rangeIds,
          round: roundIds,
          beat: beatIds,
          compartment: compartmentIds,
        });
        setSelectedRole(roleId);

        // Check if roles array exists before finding role
        if (Array.isArray(roles)) {
          const selectedRoleName = roles.find(
            (role) => role.roleId === roleId
          )?.role;
          // Update form configuration based on role
          const roleConfig =
            originalFormConfigs[selectedRoleName] ||
            originalFormConfigs["default"] ||
            [];
          setFormConfig(roleConfig);
        }
      }
    } else {
      setIsEditMode(false);
      // Initialize empty form for new user
      setFormData({});
      setSelectedDivisions([]);
      setSelectedRanges([]);
      setSelectedRounds([]);
      setSelectedBeats([]);
      setSelectedCompartments([]);
    }
  }, [userData, dispatch, roles, originalFormConfigs]);
  // Load dependent data when in edit mode
// Updated useEffect to properly convert all IDs to numbers
useEffect(() => {
  if (userData && isEditMode) {
    // Convert all IDs to appropriate number format
    const divisionIds = convertToNumberIds(userData.divisionId);
    const rangeIds = convertToNumberIds(userData.rangeId);
    const roundIds = convertToNumberIds(userData.roundId);
    const beatIds = convertToNumberIds(userData.beatId);
    // Pass the converted numbers to the loadDependentDropdowns function
    userData.divisionId && loadDependentDropdowns(divisionIds, rangeIds, roundIds, beatIds);
  }
}, [userData, isEditMode]);

  // Update selected divisions after data is loaded
  useEffect(() => {
    if(isEditMode){
    if ( divisions?.data && formData.division) {
      const divisionOptions = Array.isArray(formData.division) 
        ? formData.division.map(id => {
            const divItem = divisions.data.find(d => d.divisionId == id);
            return {
              value: id,
              label: divItem?.divisionName || divItem?.name || "All"
            };
          })
        : [];
      setSelectedDivisions(divisionOptions);
    }
    else {
      setSelectedDivisions([{ value: 0, label: "All" }]);
    }
  }
  }, [isEditMode, divisions, formData.division]);

  // Update selected ranges after data is loaded
  useEffect(() => {
    if(isEditMode){
    if ( ranges?.data && formData?.range?.length > 0) {
      const rangeOptions = Array.isArray(formData.range) 
        ? formData.range.map(id => {
            const rangeItem = ranges.data.find(r => r.rangeId == id);
            return {
              value: id,
              label: rangeItem?.rangeName || rangeItem?.name || "All"
            };
          })
        : [];
      setSelectedRanges(rangeOptions);
    }
    else {
      setSelectedRanges([{ value: 0, label: "All" }]);
    }
  }
  }, [isEditMode, ranges, formData.range]);

  // Update selected rounds after data is loaded
  useEffect(() => {
    if(isEditMode){

    if (isEditMode && rounds?.data && formData.round?.length > 0) {
      const roundOptions = Array.isArray(formData.round) 
        ? formData.round.map(id => {
            const roundItem = rounds.data.find(r => r.roundId == id);
            return {
              value: id,
              label: roundItem?.roundName || roundItem?.name || "All"
            };
          })
        : [];
      setSelectedRounds(roundOptions);
    }
    else {
      setSelectedRounds([{ value: 0, label: "All" }]);
    }
  }
  }, [isEditMode, rounds, formData.round]);

  // Update selected beats after data is loaded
  useEffect(() => {
    if(isEditMode){

    if (isEditMode && beats?.data && formData?.beat?.length > 0) {
      const beatOptions = Array.isArray(formData.beat) 
        ? formData.beat.map(id => {
            const beatItem = beats.data.find(b => b.beatId === id);
            return {
              value: id,
              label: beatItem?.beatName || beatItem?.name || "All"
            };
          })
        : [];
      setSelectedBeats(beatOptions);
    }
    else {
      setSelectedBeats([{ value: 0, label: "All" }]);
    }
  }
    
  }, [isEditMode, beats, formData.beat]);

  // Run filter compartments when selectedBeats changes
  useEffect(() => {
    filterCompartmentsByBeats();
  }, [selectedBeats, compartments]);

  // Update selected compartments after filtered compartments are updated
  useEffect(() => {
    if(isEditMode){

    if (isEditMode && compartments && formData?.compartment?.length > 0) {
      const compartmentOptions = Array.isArray(formData.compartment) 
        ? formData.compartment.map(id => {
            const compartmentItem = compartments?.find(c => c.compId == id);
            return {
              value: id,
              label: compartmentItem?.compId|| "All"
            };
          })
        : [];
      setSelectedCompartments(compartmentOptions);
    }
    else {
      setSelectedCompartments([{ value: 0, label: "All" }]);
    }
  }
  }, [isEditMode, compartments, formData.compartment]);

  // Fetch initial form configuration
  useEffect(() => {
    try {
      const storedFormResponse = JSON.parse(
        localStorage.getItem("formResponse") || "[]"
      );
      if (!Array.isArray(storedFormResponse)) {
        console.error("Invalid form configuration");
        return;
      }

      const formConfigsByRole = {};
      storedFormResponse.forEach((form) => {
        if (form.formId === "user_registration_form") {
          // Check if roles is an array before iterating
          if (Array.isArray(form.roles)) {
            form.roles.forEach((roleId) => {
              formConfigsByRole[roleId] = form.fields;
            });
          } else if (form.roles) {
            // Handle case where roles might be a single value
            formConfigsByRole[form.roles] = form.fields;
          }
        }
      });

      // If no role-specific configurations, use default
      if (Object.keys(formConfigsByRole).length === 0) {
        const defaultForm = storedFormResponse.find(
          (form) => form.formId === "user_registration_form"
        );
        if (defaultForm && defaultForm.fields) {
          formConfigsByRole["default"] = defaultForm.fields;
        }
      }
      setOriginalFormConfigs(formConfigsByRole);

      // Fetch initial data if not already loaded
      if (!roles || !Array.isArray(roles) || roles.length === 0)
        dispatch(fetchRoles());
      if (!divisions || !divisions.data) dispatch(fetchDivisions());

      // Set form config when user data and role are present
      if (
        userData &&
        userData.roleId &&
        Object.keys(formConfigsByRole).length > 0
      ) {
        if (Array.isArray(roles)) {
          const roleName = roles.find(
            (r) => r.roleId === userData.roleId
          )?.role;
          const roleConfig =
            formConfigsByRole[roleName] || formConfigsByRole["default"] || [];
          setFormConfig(roleConfig);
        }
      }
    } catch (error) {
      console.error("Error processing form configuration:", error);
    }
  }, [dispatch, userData, roles]);

  // Handle success and error messages
  useEffect(() => {
    if (message) {
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        if (onSuccess) onSuccess();
        toggle(); // Close the popup on success
      }, 2000);
    }
  }, [message, onSuccess, toggle]);

  useEffect(() => {
    if (toastMessage) {
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 2000);
    }
  }, [toastMessage]);

  useEffect(() => {
    if (registrationError) {
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    }
  }, [ registrationError]);

  // Handle role change
  const handleRoleChange = (e) => {
    const roleId = e.target.value;
    setSelectedRole(roleId);

    // Update form data while preserving other fields in edit mode
    setFormData((prev) => ({
      ...(isEditMode ? prev : {}),
      role: roleId,
    }));

    if (Array.isArray(roles)) {
      const selectedRoleName = roles.find(
        (role) => role.roleId === roleId
      )?.role;
      // Update form configuration based on role
      const roleConfig =
        originalFormConfigs[selectedRoleName] ||
        originalFormConfigs["default"] ||
        [];
      setFormConfig(roleConfig);
    }
  };

  // Fix for infinite update loop - separated useEffect for onsubmit
  useEffect(() => {
    // Only trigger submission if onsubmit is true and we haven't already processed it
    if (isEditMode && onsubmit && !submitTriggered) {
      setSubmitTriggered(true); // Mark as triggered to prevent future calls
      handleSubmit(formData);
      if (setUpdated) {
        setUpdated(false); // Reset the parent's state
      }
    }
  }, [onsubmit, isEditMode]);

  // Reset submitTriggered when onsubmit becomes false
  useEffect(() => {
    if (!onsubmit) {
      setSubmitTriggered(false);
    }
  }, [onsubmit]);

  // Form submission handler
  const handleSubmit = async (values) => {
    const {email, role, division, range, round, beat, compartment, ...otherData} = values;
    // Join arrays with # for backend format
    const divisionIdString = Array.isArray(formData.division) 
      ? formData.division?.length==0 ? "0": formData.division.join("#") 
      : formData.division || "0";
      const normalizeField = (value) =>
        Array.isArray(value)
          ? value.length === 0 ? [0] : value
          : (value === '' || value == null) ? [0] : value;

          
    const payload = {
      ...otherData,
      username: email?.toLowerCase(),
      roleId: selectedRole,
      divisionId: divisionIdString,
      rangeId: normalizeField(formData.range),
  roundId: normalizeField(formData.round),
  beatId: normalizeField(formData.beat),
  comptId: normalizeField(formData.compartment),
    };

    
    if (isEditMode) {
      try {
        const response = await postLogin({
          act: "user",
          subact: "updateuser",
          info: payload,
        });
        toast.success(response?.message || "User updated successfully", {
          position: "top-right",
          autoClose: 3000,
        });

        dispatch(getAllUSer());
        if (onSuccess) onSuccess();
      } catch (error) {
        toast.error(error.response?.data?.message || "Update failed", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } else {
      try {
        const response = await postLogin({
          act: "user",
          subact: "create",
          info: payload,
        });
        if (response.message) {
          toast.success(response?.message || "User created successfully", {
            position: "top-right",
            autoClose: 3000,
          });
        }
        setFormData({});
        setSelectedRole(null);
        setSelectedDivisions([]);
        setSelectedRanges([]);
        setSelectedRounds([]);
        setSelectedBeats([]);
        setSelectedCompartments([]);
        if (onSuccess) onSuccess();
      } catch (error) {
        toast.error(error.response?.data?.message || "Creation failed", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    }
  };

  // Transform divisions data for react-select
  const getDivisionOptions = () => {
    if (!Array.isArray(divisions?.data)) return [];
    return divisions.data.map(division => ({
      value: Number(division.divisionId),
      label: division.divisionName || division.name
    }));
  };

  // Transform ranges data for react-select
  const getRangeOptions = () => {
    if (!Array.isArray(ranges?.data)) return [];
    return ranges.data.map(range => ({
      value: range.rangeId,
      label: range.rangeName || range.name
    }));
  };

  // Transform rounds data for react-select
  const getRoundOptions = () => {
    if (!Array.isArray(rounds?.data)) return [];
    return rounds.data.map(round => ({
      value: round.roundId,
      label: round.roundName || round.name
    }));
  };

  // Transform beats data for react-select
  const getBeatOptions = () => {
    if (!Array.isArray(beats?.data)) return [];
    return beats.data.map(beat => ({
      value: beat.beatId,
      label: beat.beatName || beat.name
    }));
  };

  // Transform compartments data for react-select, filtering by selected beats
  const getCompartmentOptions = () => {
    if (!filteredCompartments || filteredCompartments.length === 0) return [];
    
    return filteredCompartments.map(compartment => ({
      value: compartment.compId || compartment.compartmentId,
      label: compartment.compartmentName || compartment.compId || compartment.compartmentId
    }));
  };

  // Handle divisions multiselect change
  const handleDivisionsChange = (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      setSelectedDivisions([]);
      setFormData(prev => ({
        ...prev,
        division: [],
        divisionName: "",
      }));
      
      // Reset dependent fields
      setSelectedRanges([]);
      setSelectedRounds([]);
      setSelectedBeats([]);
      setSelectedCompartments([]);
      setFormData(prev => ({
        ...prev,
        range: [],
        rangeName: "",
        round: [],
        roundName: "",
        beat: [],
        beatName: "",
        compartment: [],
        compartmentName: "",
      }));
      return;
    }
    selectedOptions= selectedOptions.filter(option => option.label !== "All");

    
    setSelectedDivisions(selectedOptions);
    
    // Extract just the values for the form data
    const divisionIds = selectedOptions.map(option => option.value);
    
    setFormData(prev => ({
      ...prev,
      division: divisionIds,
      divisionName: selectedOptions.map(option => option.label).join(", ")
    }));

    dispatch(fetchRanges(divisionIds));


    // Reset dependent fields
    setSelectedRanges([]);
    setSelectedRounds([]);
    setSelectedBeats([]);
    setSelectedCompartments([]);
    setFormData(prev => ({
      ...prev,
      range: [],
      rangeName: "",
      round: [],
      roundName: "",
      beat: [],
      beatName: "",
      compartment: [],
      compartmentName: "",
    }));
  };

  // Handle ranges multiselect change
  const handleRangesChange = (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      setSelectedRanges([]);
      setFormData(prev => ({
        ...prev,
        range: [],
        rangeName: "",
      }));
      
      // Reset dependent fields
      setSelectedRounds([]);
      setSelectedBeats([]);
      setSelectedCompartments([]);
      setFormData(prev => ({
        ...prev,
        round: [],
        roundName: "",
        beat: [],
        beatName: "",
        compartment: [],
        compartmentName: "",
      }));
      return;
    }
    selectedOptions= selectedOptions.filter(option => option.label !== "All");
    
    setSelectedRanges(selectedOptions);
    
    // Extract just the values for the form data
    const rangeIds = selectedOptions.map(option => option.value);
    
    setFormData(prev => ({
      ...prev,
      range: rangeIds,
      rangeName: selectedOptions.map(option => option.label).join(", ")
    }));


        dispatch(fetchRounds(rangeIds));
 

    // Reset dependent fields
    setSelectedRounds([]);
    setSelectedBeats([]);
    setSelectedCompartments([]);
    setFormData(prev => ({
      ...prev,
      round: [],
      roundName: "",
      beat: [],
      beatName: "",
      compartment: [],
      compartmentName: "",
    }));
  };

  // Handle rounds multiselect change
  const handleRoundsChange = (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      setSelectedRounds([]);
      setFormData(prev => ({
        ...prev,
        round: [],
        roundName: "",
      }));
      
      // Reset dependent fields
      setSelectedBeats([]);
      setSelectedCompartments([]);
      setFormData(prev => ({
        ...prev,
        beat: [],
        beatName: "",
        compartment: [],
        compartmentName: "",
      }));
      return;
    }
    selectedOptions= selectedOptions.filter(option => option.label !== "All");

    setSelectedRounds(selectedOptions);
    
    // Extract just the values for the form data
    const roundIds = selectedOptions.map(option => option.value);
    
    setFormData(prev => ({
      ...prev,
      round: roundIds,
      roundName: selectedOptions.map(option => option.label).join(", ")
    }));

        dispatch(fetchBeats(roundIds));
   
    // Reset dependent fields
    setSelectedBeats([]);
    setSelectedCompartments([]);
    setFormData(prev => ({
      ...prev,
      beat: [],
      beatName: "",
      compartment: [],
      compartmentName: "",
    }));
  };

  // Handle beats multiselect change
  const handleBeatsChange = (selectedOptions) => {
    // Handle null or empty selection
    if (!selectedOptions || selectedOptions.length === 0) {
      setSelectedBeats([]);
      setFormData(prev => ({
        ...prev,
        beat: [],
        beatName: "",
      }));
      
      // Reset compartments as well when beats are cleared
      setSelectedCompartments([]);
      setFormData(prev => ({
        ...prev,
        compartment: [],
        compartmentName: "",
      }));
      return;
    }
    selectedOptions= selectedOptions.filter(option => option.label !== "All");

    setSelectedBeats(selectedOptions);
    
    // Extract just the values for the form data
    const beatIds = selectedOptions.map(option => option.value);
    
    setFormData(prev => ({
      ...prev,
      beat: beatIds,
      beatName: selectedOptions.map(option => option.label).join(", ")
    }));

    // Reset compartments when beats change
    setSelectedCompartments([]);
    setFormData(prev => ({
      ...prev,
      compartment: [],
      compartmentName: ""
    }));
  };

  // Handle compartments multiselect change
  const handleCompartmentsChange = (selectedOptions) => {
    // Handle null or empty selection
    if (!selectedOptions || selectedOptions.length === 0) {
      setSelectedCompartments([]);
      setFormData(prev => ({
        ...prev,
        compartment: [],
        compartmentName: ""
      }));
      return;
    }
    selectedOptions= selectedOptions.filter(option => option.label !== "All");

    setSelectedCompartments(selectedOptions);
    
    // Extract just the values for the form data
    const compartmentIds = selectedOptions.map(option => option.value);
    
    setFormData(prev => ({
      ...prev,
      compartment: compartmentIds,
      compartmentName: selectedOptions.map(option => option.label).join(", ")
    }));
  };


  return (
    <div className={!isEditMode ? "page-content" : ""}>
      <Container fluid>
        {!isEditMode ? (
          <Breadcrumbs title="Control Room" breadcrumbItem="Register User" />
        ) : (
          ""
        )}

        <div className="user-form-popup">
          <Card>
            <CardBody>
              <h4 className="mb-2">{isEditMode ? "Edit User" : "New User"}</h4>
              {/* Role Selection */}
              <FormGroup>
                <Label for="role">Role</Label>
                <Input
                  type="select"
                  id="role"
                  disabled={isEditMode}
                  value={formData.role || ""}
                  onChange={handleRoleChange}
                >
                  <option value="">Select Role</option>
                  {Array.isArray(roles) &&
                    roles.map(({ roleId, role }) => (
                      <option key={roleId} value={roleId}>
                        {role}
                      </option>
                    ))}
                </Input>
              </FormGroup>

              {/* Multiselect Dropdowns for Divisions, Ranges, Rounds */}
              <Row>
                {/* Division Multi-select */}
                <Col md={isEditMode? 4:2}>
                  <FormGroup>
                    <Label for="division">Division</Label>
                    <Select
                      isMulti
                      name="divisions"
                      id="division"
                      value={selectedDivisions}
                      onChange={handleDivisionsChange}
                      options={getDivisionOptions()}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      isDisabled={!selectedRole}
                      placeholder="Select Divisions..."
                      isClearable={true}
                    />
                  </FormGroup>
                </Col>

                {/* Range Multi-select */}
                <Col md={isEditMode? 4:2}>
                  <FormGroup>
                    <Label for="range">Range</Label>
                    <Select
                      isMulti
                      name="ranges"
                      id="range"
                      value={selectedRanges}
                      onChange={handleRangesChange}
                      options={getRangeOptions()}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      isDisabled={!selectedRole || selectedDivisions.length === 0 }
                      placeholder="Select Ranges..."
                      isClearable={true}
                    />
                  </FormGroup>
                </Col>

                {/* Round Multi-select */}
                <Col md={isEditMode? 4:2}>
                  <FormGroup>
                    <Label for="round">Round</Label>
                    <Select
                      isMulti
                      name="rounds"
                      id="round"
                      value={selectedRounds}
                      onChange={handleRoundsChange}
                      options={getRoundOptions()}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      isDisabled={!selectedRole || selectedRanges.length === 0 }
                      placeholder="Select Rounds..."
                      isClearable={true}
                    />
                  </FormGroup>
                </Col>
                
                {/* Beat Multi-select */}
                <Col md={isEditMode? 6:3}>
                  <FormGroup>
                    <Label for="beat">Beat</Label>
                    <Select
                      isMulti
                      name="beats"
                      id="beat"
                      value={selectedBeats}
                      onChange={handleBeatsChange}
                      options={getBeatOptions()}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      isDisabled={
                        !selectedRole || 
                        selectedRounds.length === 0 ||
                        (isEditMode && (!formData.division || formData.division.length === 0)) 
                      }
                      placeholder="Select Beats..."
                      isClearable={true}
                    />
                  </FormGroup>
                </Col>

                {/* Compartment Multi-select */}
                <Col md={isEditMode?6:3}>
                  <FormGroup>
                    <Label for="compartment">Compartment</Label>
                    <Select
                      isMulti
                      name="compartments"
                      id="compartment"
                      value={selectedCompartments}
                      onChange={handleCompartmentsChange}
                      options={getCompartmentOptions()}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      isDisabled={
                        !selectedRole || 
                        selectedBeats.length === 0 ||
                        (isEditMode && (!formData.division || formData.division.length === 0)) 
                      }
                      placeholder="Select Compartments..."
                      isClearable={true}
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* Dynamic Form Fields */}
              {selectedRole &&
                Array.isArray(formConfig) &&
                formConfig.length > 0 && (
                  <CommonForm
                    formConfig={formConfig}
                    initialValues={formData}
                    setFormData={setFormData}
                    validationSchema={generateValidationSchema(formConfig)}
                    onSubmit={handleSubmit}
                    isEditMode={isEditMode}
                    submitText={isEditMode ? "" : "Register"}
                  />
                )}
              {/* Success and Error Alerts */}
              {showSuccess && (
                <SuccessAlert message={toastMessage || message} />
              )}
              {showError && <ErrorAlert error={error || registrationError} />}
            </CardBody>
          </Card>
        </div>
      </Container>
    </div>
  );
};

export default UserForm;